import { useState } from "react";
import { X, Loader2, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import RichContent from "@/components/RichContent";

export default function AgreementSignModal({ agreement, onClose, onSigned }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [signature, setSignature] = useState(user?.name || "");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!agreement) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Please log in to sign."); nav("/login"); return; }
    setError("");
    setLoading(true);
    try {
      await api.post(`/agreements/${agreement.id}/sign`, { name: user.name || signature, signature, consent });
      toast.success("Agreement signed.");
      onSigned && onSigned();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center px-4 bg-black/40 backdrop-blur-sm overflow-y-auto py-10" data-testid="agreement-sign-modal">
      <div className="relative w-full max-w-lg bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        <p className="eyw-label mb-1 flex items-center gap-2"><FileText size={14} /> {agreement.type} · v{agreement.version}</p>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-4">{agreement.title}</h3>
        {agreement.pdf_path && (
          <a href={`/api/files/${agreement.pdf_path}`} target="_blank" rel="noreferrer" data-testid="agreement-pdf-link" className="mb-2 inline-flex text-sm text-[#144F75] underline">Read the full document (PDF)</a>
        )}
        <div className="max-h-56 overflow-y-auto rounded-xl border border-[#E5E0D8] bg-white p-4 text-sm text-[#4A5D6B] leading-relaxed" data-testid="agreement-content">
          <RichContent text={agreement.content} />
        </div>
        {!user ? (
          <div className="mt-5">
            <p className="text-sm text-[#4A5D6B] mb-3">Log in or join to sign this agreement electronically.</p>
            <button onClick={() => { onClose(); nav("/login"); }} data-testid="agreement-login-cta" className="eyw-btn w-full">Log in to sign</button>
          </div>
        ) : (
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <label className="text-xs text-[#4A5D6B] mb-1 block">Type your full name to sign</label>
            <input required value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="agreement-signature"
              className="eyw-input w-full font-serif text-lg" placeholder="Your full name" />
          </div>
          <label className="flex items-start gap-2 text-xs text-[#4A5D6B]">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="agreement-consent" className="mt-0.5" />
            I have read and agree to this {agreement.title}, and my typed name is my electronic signature.
          </label>
          {error && <p className="text-sm text-red-600" data-testid="agreement-error">{error}</p>}
          <button type="submit" disabled={loading || !consent} data-testid="agreement-sign-submit" className="eyw-btn w-full disabled:opacity-50">
            {loading && <Loader2 className="animate-spin" size={18} />} Sign Agreement
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
